import { Controller } from "@hotwired/stimulus"
import { refreshTrixEditors } from "../utils/refresh_trix_editors"

// Connects to data-controller="comments"
export default class extends Controller {
  static targets = ["newCommentForm", "newCommentFormTemplate", "newCommentToggle"]

  connect() {
    // Scroll to bottom when modal opens to show the form
    this.scrollToBottom();
    this.bindModalEvents();
    
    // Listen for turbo frame loads to handle edit form visibility
    this.boundHandleFrameLoad = this.handleFrameLoad.bind(this);
    this.boundHandleFrameClear = this.handleFrameClear.bind(this);
    this.boundHandleStreamRender = this.handleStreamRender.bind(this);
    
    // Listen for turbo frame loads on all comment edit frames
    document.addEventListener('turbo:frame-load', this.boundHandleFrameLoad);
    document.addEventListener('turbo:before-stream-render', this.boundHandleFrameClear);
    document.addEventListener('turbo:stream-render', this.boundHandleStreamRender);
  }

  disconnect() {
    document.removeEventListener('turbo:frame-load', this.boundHandleFrameLoad);
    document.removeEventListener('turbo:before-stream-render', this.boundHandleFrameClear);
    document.removeEventListener('turbo:stream-render', this.boundHandleStreamRender);

    const modal = this.element.closest('.modal');
    if (modal && this.boundModalShown) {
      modal.removeEventListener('shown.bs.modal', this.boundModalShown);
    }
  }

  bindModalEvents() {
    const modal = this.element.closest('.modal');
    if (!modal) return;

    this.boundModalShown = () => refreshTrixEditors(this.element);
    modal.addEventListener('shown.bs.modal', this.boundModalShown);
  }

  handleFrameLoad(event) {
    // Check if this is an edit frame being loaded
    const frame = event.target;
    if (frame.tagName === 'TURBO-FRAME' && frame.id && frame.id.includes('_edit')) {
      const commentId = frame.id.match(/comment_(\d+)_edit/)?.[1];
      if (commentId) {
        // Hide the comment body when edit form appears
        const commentItem = document.querySelector(`[data-comment-id="${commentId}"]`);
        if (commentItem) {
          const commentBody = commentItem.querySelector('.comment-body');
          if (commentBody) {
            commentBody.style.display = 'none';
          }
        }
      }
    }
  }

  handleFrameClear(event) {
    // When turbo stream replaces the comment item, the body will be shown again
    // This handles the case when edit is cancelled or saved
  }

  handleStreamRender(event) {
    // When a new comment is added via turbo stream, expand the parent conversation if it exists
    setTimeout(() => {
      const newComment = event.target.querySelector('[data-comment-id]');
      if (newComment) {
        const commentId = newComment.getAttribute('data-comment-id');
        // Find if this is a response (has a parent)
        const responseElement = document.querySelector(`[data-comment-id="${commentId}"].conversation-response`);
        if (responseElement) {
          // Find the parent conversation and expand it
          const conversationItem = responseElement.closest('.conversation-item');
          if (conversationItem) {
            const conversationController = this.application.getControllerForElementAndIdentifier(
              conversationItem,
              'conversation'
            );
            if (conversationController) {
              conversationController.expand();
            }
          }
        }
      }
    }, 100);
  }

  toggleReplyForm(event) {
    event.preventDefault();
    const replyFormId = event.params.replyFormId;
    const replyForm = document.getElementById(replyFormId);
    
    if (replyForm) {
      const isHidden = replyForm.style.display === 'none';
      
      // Hide all other reply forms first
      this.hideAllReplyForms();
      
      // If replying to a thread comment, expand the conversation first
      const conversationItem = replyForm.closest('.conversation-item');
      if (conversationItem) {
        const conversationController = this.application.getControllerForElementAndIdentifier(
          conversationItem,
          'conversation'
        );
        if (conversationController) {
          conversationController.expand();
        }
      }
      
      if (isHidden) {
        replyForm.style.display = 'block';
        refreshTrixEditors(replyForm);
        // Focus on the textarea/trix editor
        const trixEditor = replyForm.querySelector('trix-editor');
        if (trixEditor) {
          setTimeout(() => trixEditor.focus(), 100);
        }
      } else {
        replyForm.style.display = 'none';
      }
    }
  }

  hideReplyForm(event) {
    event.preventDefault();
    const replyFormId = event.params.replyFormId;
    const replyForm = document.getElementById(replyFormId);
    
    if (replyForm) {
      replyForm.style.display = 'none';
      // Clear the form
      const trixEditor = replyForm.querySelector('trix-editor');
      if (trixEditor && trixEditor.editor) {
        trixEditor.editor.loadHTML('');
      }
    }
  }

  hideAllReplyForms() {
    const allReplyForms = document.querySelectorAll('[id^="reply-form-"]');
    allReplyForms.forEach(form => {
      form.style.display = 'none';
    });
  }

  scrollToBottom() {
    const modalBody = this.element.closest('.modal-body');
    if (modalBody) {
      setTimeout(() => {
        modalBody.scrollTop = modalBody.scrollHeight;
      }, 100);
    }
  }

  scrollToNewComment() {
    // Scroll to show the newly added comment
    this.scrollToBottom();
  }

  cancelEdit(event) {
    event.preventDefault();
    const commentId = event.params.commentId;
    // dom_id generates IDs like "edit_comment_123" for turbo frames
    const editFrame = document.querySelector(`turbo-frame[id="edit_comment_${commentId}"]`);
    
    if (editFrame) {
      // Clear the turbo frame content to hide the edit form
      editFrame.innerHTML = '';      
      // Show the comment body again
      const commentItem = document.querySelector(`[data-comment-id="${commentId}"]`);
      if (commentItem) {
        const commentBody = commentItem.querySelector('.comment-body');
        if (commentBody) {
          commentBody.style.display = '';
        }
      }
    }
  }

  mountNewCommentForm() {
    if (!this.hasNewCommentFormTarget || this.newCommentFormTarget.dataset.mounted === 'true') return;

    if (this.hasNewCommentFormTemplateTarget) {
      this.newCommentFormTarget.appendChild(
        this.newCommentFormTemplateTarget.content.cloneNode(true)
      );
    }

    this.newCommentFormTarget.dataset.mounted = 'true';
  }

  clearNewCommentForm() {
    if (!this.hasNewCommentFormTarget) return;

    this.newCommentFormTarget.innerHTML = '';
    delete this.newCommentFormTarget.dataset.mounted;
    this.newCommentFormTarget.style.display = 'none';
  }

  toggleNewCommentForm(event) {
    event.preventDefault();
    if (this.hasNewCommentFormTarget) {
      const isHidden = this.newCommentFormTarget.style.display === 'none';

      if (this.hasNewCommentToggleTarget) {
        if (isHidden) {
          this.newCommentToggleTarget.textContent = 'Annuler';
        } else {
          this.newCommentToggleTarget.textContent = 'Nouvelle discussion';
        }
      }

      if (isHidden) {
        this.newCommentFormTarget.style.display = 'block';
        this.mountNewCommentForm();
        refreshTrixEditors(this.newCommentFormTarget);

        setTimeout(() => {
          const subjectField = this.newCommentFormTarget.querySelector('input[name*="subject"]');
          const bodyField = this.newCommentFormTarget.querySelector('trix-editor');
          if (subjectField) {
            subjectField.focus();
          } else if (bodyField) {
            bodyField.focus();
          }
        }, 100);
      } else {
        this.clearNewCommentForm();
      }
    }
  }
}
